import { useEffect, useRef, useState } from 'react'
import 'leaflet/dist/leaflet.css'
import { parcelsAdminApi, type ParcelSource, type ParcelHealth, type ParcelJurisdiction } from '../../lib/api'

// Escape text before it reaches a Leaflet tooltip/popup, which renders a string
// as innerHTML. Mirrors escTip() in FarmMapPage.
function escHtml(v: unknown): string {
  return String(v ?? '').replace(/[&<>"']/g, c => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string))
}

// Superadmin "Maps" tab — a US map of every wired parcel-source API for at-a-glance
// coverage + live troubleshooting. The server pings each source (by code) from the
// deployed egress (which can differ from a laptop), so this is a real diagnostic tool.

const C = {
  text:    'var(--color-text-primary)',
  muted:   'var(--color-text-secondary)',
  faint:   'var(--color-text-tertiary, #888)',
  card:    'var(--color-background-secondary, #1A2212)',
  border:  'var(--color-border, #2a3320)',
  bg:      'var(--color-background-primary, #0E1209)',
}
const STATUS = { untested: '#9CA3AF', ok: '#22C55E', fail: '#EF4444', testing: '#F5A623' }

type Status = keyof typeof STATUS
function statusOf(h?: ParcelHealth, testing?: boolean): Status {
  if (testing) return 'testing'
  if (!h) return 'untested'
  return h.ok ? 'ok' : 'fail'
}

export function ParcelsMapPanel() {
  const [sources, setSources] = useState<ParcelSource[]>([])
  const [health, setHealth] = useState<Record<string, ParcelHealth>>({})
  const [testing, setTesting] = useState<Record<string, boolean>>({})
  const [selected, setSelected] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [coverage, setCoverage] = useState<ParcelJurisdiction[]>([])
  const [covSummary, setCovSummary] = useState<{ statewide: number; county_only: number; none: number; total: number } | null>(null)

  const mapEl = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const layerRef = useRef<any>(null)
  const markerByCode = useRef<Record<string, any>>({})

  // Load sources
  useEffect(() => {
    parcelsAdminApi.sources()
      .then(r => setSources(r.data.sources || []))
      .catch(() => setErr('Failed to load parcel sources'))
    parcelsAdminApi.coverage()
      .then(r => { setCoverage(r.data.jurisdictions || []); setCovSummary(r.data.summary) })
      .catch(() => { /* coverage table is best-effort */ })
  }, [])

  // Init Leaflet map once
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const L = (await import('leaflet')).default
      if (cancelled || !mapEl.current || mapRef.current) return
      const map = L.map(mapEl.current, { center: [39.5, -98.35], zoom: 4, scrollWheelZoom: true })
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
        { attribution: 'Esri', maxZoom: 16 },
      ).addTo(map)
      mapRef.current = map
      layerRef.current = L.layerGroup().addTo(map)
      renderMarkers(L)
    })()
    return () => { cancelled = true; if (mapRef.current) { mapRef.current.remove(); mapRef.current = null } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-render markers whenever sources / health / selection change
  useEffect(() => {
    if (!mapRef.current) return
    ;(async () => { const L = (await import('leaflet')).default; renderMarkers(L) })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources, health, testing, selected])

  function renderMarkers(L: any) {
    if (!layerRef.current) return
    layerRef.current.clearLayers()
    markerByCode.current = {}
    for (const s of sources) {
      const st = statusOf(health[s.code], testing[s.code])
      const color = STATUS[st]
      const isSel = selected === s.code
      const m = L.circleMarker([s.lat, s.lng], {
        radius: s.kind === 'state' ? 8 : 6,
        color: isSel ? '#fff' : color,
        weight: isSel ? 3 : 1.5,
        fillColor: color,
        fillOpacity: 0.85,
      })
      // Leaflet renders a STRING tooltip as innerHTML. `health[].error` is built
      // from a THIRD-PARTY parcel/GIS server's response body, so an attacker who
      // controls (or MITMs) a registered source could return an error string
      // containing markup and pop script in the SUPERADMIN console — where the
      // JWT has cross-tenant reach. Escape both interpolated values.
      m.bindTooltip(
        `${escHtml(s.name)}${health[s.code]?.error ? ' — ' + escHtml(health[s.code]!.error) : ''}`,
        { direction: 'top' },
      )
      m.on('click', () => setSelected(s.code))
      m.addTo(layerRef.current)
      markerByCode.current[s.code] = m
    }
  }

  async function testOne(code: string) {
    setTesting(t => ({ ...t, [code]: true }))
    try {
      const r = await parcelsAdminApi.health(code)
      setHealth(h => ({ ...h, [code]: r.data }))
    } catch {
      setHealth(h => ({ ...h, [code]: { code, ok: false, http_status: 0, latency_ms: 0, error: 'request failed' } }))
    } finally {
      setTesting(t => { const n = { ...t }; delete n[code]; return n })
    }
  }
  function testAll() { sources.forEach(s => testOne(s.code)) }

  function selectRow(s: ParcelSource) {
    setSelected(s.code)
    if (mapRef.current) mapRef.current.setView([s.lat, s.lng], s.kind === 'county' ? 8 : 6, { animate: true })
  }

  const okCount = Object.values(health).filter(h => h.ok).length
  const failCount = Object.values(health).filter(h => !h.ok).length
  const states = sources.filter(s => s.kind === 'state')
  const counties = sources.filter(s => s.kind === 'county')

  return (
    <div style={{ color: C.text, fontFamily: '"DM Sans", system-ui, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Parcel source map</h2>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: C.muted }}>
            {states.length} statewide + {counties.length} county parcel APIs. Tested from the server's egress.
            {(okCount + failCount > 0) && <> · <span style={{ color: STATUS.ok }}>{okCount} up</span> / <span style={{ color: STATUS.fail }}>{failCount} down</span></>}
          </p>
        </div>
        <button onClick={testAll} style={{ marginLeft: 'auto', padding: '8px 16px', borderRadius: 8, background: 'var(--color-primary, #F5A623)', border: 'none', color: '#0E1209', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          Test all APIs
        </button>
      </div>

      {err && <div style={{ fontSize: 13, color: STATUS.fail, marginBottom: 10 }}>{err}</div>}

      <div ref={mapEl} style={{ height: 420, width: '100%', borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: 8 }} />

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: C.muted, marginBottom: 16 }}>
        {(['untested', 'ok', 'fail', 'testing'] as Status[]).map(s => (
          <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 11, height: 11, borderRadius: '50%', background: STATUS[s], display: 'inline-block' }} />
            {s === 'ok' ? 'reachable + queryable' : s === 'fail' ? 'down / not queryable' : s}
          </span>
        ))}
      </div>

      {/* Source table */}
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
        {sources.map(s => {
          const h = health[s.code]
          const st = statusOf(h, testing[s.code])
          const sel = selected === s.code
          return (
            <div key={s.code} onClick={() => selectRow(s)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderTop: `1px solid ${C.border}`, cursor: 'pointer', background: sel ? 'color-mix(in srgb, var(--color-primary, #F5A623) 12%, transparent)' : 'transparent' }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: STATUS[st], flexShrink: 0 }} />
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: C.faint, width: 52, flexShrink: 0 }}>{s.kind}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{s.name} <span style={{ color: C.faint, fontWeight: 400 }}>· {s.code}</span></div>
                {h && (
                  <div style={{ fontSize: 11, color: h.ok ? C.muted : STATUS.fail, marginTop: 1 }}>
                    {h.ok ? `OK · ${h.latency_ms}ms${h.capabilities ? ' · ' + h.capabilities : ''}` : `FAIL · ${h.error || 'HTTP ' + h.http_status}`}
                  </div>
                )}
                {s.terms && <div style={{ fontSize: 11, color: C.faint, marginTop: 1 }}>terms: {s.terms}</div>}
              </div>
              <button
                onClick={e => { e.stopPropagation(); testOne(s.code) }}
                disabled={!!testing[s.code]}
                style={{ padding: '5px 12px', borderRadius: 7, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
                {testing[s.code] ? 'Testing…' : 'Test'}
              </button>
            </div>
          )
        })}
        {sources.length === 0 && !err && <div style={{ padding: 16, fontSize: 13, color: C.faint }}>Loading sources…</div>}
      </div>

      {/* Jurisdiction coverage table — every US state + DC + territory, statewide/county/none */}
      {coverage.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ margin: '0 0 3px', fontSize: 16, fontWeight: 700 }}>Jurisdiction coverage</h2>
          <p style={{ margin: '0 0 10px', fontSize: 13, color: C.muted }}>
            All US states + DC + territories. Derived live from the registry.
            {covSummary && <> · <span style={{ color: STATUS.ok }}>{covSummary.statewide} statewide</span> · {covSummary.county_only} county-only · <span style={{ color: STATUS.fail }}>{covSummary.none} none</span></>}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
            {coverage.map(j => {
              const color = j.status === 'none' ? STATUS.fail : j.status === 'statewide' ? STATUS.ok : j.status === 'statewide+county' ? '#34D399' : '#60A5FA'
              const label = j.status === 'none' ? 'none yet' : j.status === 'county' ? `${j.county_count} count${j.county_count === 1 ? 'y' : 'ies'}` : j.status === 'statewide+county' ? 'statewide + county' : 'statewide'
              return (
                <div key={j.code} title={j.statewide_source || (j.counties || []).join(', ')}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                  <span style={{ fontSize: 10, fontWeight: 700, color: C.faint, width: 24, flexShrink: 0 }}>{j.code}</span>
                  <span style={{ fontSize: 13, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.name}</span>
                  <span style={{ fontSize: 11, color: color, flexShrink: 0 }}>{label}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
